/**
 * 统一的API服务 - 整合所有API调用
 */

import { surveyApi } from './modules/survey.js'
import { wechatApi } from './modules/wechat.js'

// 统一的API服务
export const apiService = {
  // 病历采集相关接口
  
  /**
   * 开始病历采集
   * @returns {Promise<Object>} 会话数据
   */
  async startSurvey() {
    const response = await surveyApi.startSession()
    console.log('🔧 开始病历采集API响应:', response)
    return this.extractResponseData(response)
  },

  /**
   * 发送文字回答
   * @param {Object} data
   * @param {string} data.session_id - 会话ID
   * @param {string} data.user_message - 用户消息
   */
  async sendTextAnswer(data) {
    const response = await surveyApi.sendTextAnswer(data)
    console.log('🔧 发送文字回答API响应:', response)
    return this.extractResponseData(response)
  },

  /**
   * 上传微信录音获取文件名（新接口）
   * @param {Object} params
   * @param {string} params.mediaId - 微信录音mediaId
   * @param {string} [params.accessToken] - 访问令牌（可选）
   * @returns {Promise<string>} 返回文件名
   */
  async uploadWechatAudio(params) {
    const response = await wechatApi.uploadAudio(params)
    console.log('🔧 上传微信录音API响应:', response)
    // 这里按统一 {code, data, msg} 规则解析，data 直接是文件名
    return this.extractResponseData(response)
  },

  /**
   * 发送语音回答
   * @param {Object|FormData} data
   * - JSON 形式：{ session_id: string, audio_filename: string }（旧格式，向后兼容）
   * - FormData 形式：包含 session_id 和 file 字段（新格式，推荐）
   */
  async sendAudioAnswer(data) {
    const response = await surveyApi.sendAudioAnswer(data)
    console.log('🔧 发送语音回答API响应:', response)
    return this.extractResponseData(response)
  },

  /**
   * 获取对话结果（包含二维码）
   * @param {Object} data
   * @param {string} data.session_id - 会话ID
   */
  async getConversationResult(data) {
    const response = await surveyApi.getConversationResult(data)
    console.log('🔧 获取对话结果API响应:', response)
    return this.extractResponseData(response)
  },

  /**
   * 统一的响应数据提取方法
   * 支持两种格式：
   * - 问卷服务: {code: 0, data: {...}, msg: ""}  (code=0表示成功)
   * - 其他服务: {code: 200, message: "...", data: {...}}  (code=200表示成功)
   */
  extractResponseData(response) {
    console.log('🔧 extractResponseData 处理数据:', response)

    // 如果是 Axios 响应格式 {data, status, statusText, headers, config}
    if (response.status && response.data !== undefined) {
      console.log('🔍 检测到Axios响应格式，提取response.data')
      return this.extractResponseData(response.data)
    }

    // 接口响应格式处理
    if (typeof response.code !== 'undefined') {
      // 问卷服务：code=0 表示成功
      if (response.code === 0) {
        console.log('✅ 请求成功（code=0），返回data')
        return response.data || {}
      }
      // 其他服务：code=200 表示成功
      else if (response.code === 200) {
        console.log('✅ 请求成功（code=200），返回data')
        return response.data || {}
      }
      // 失败情况
      else {
        const errorMsg = response.message || response.msg || '请求失败'
        console.error('❌ 请求失败:', errorMsg)
        throw new Error(errorMsg)
      }
    }

    // 降级处理：直接返回
    console.warn('⚠️ 未知响应格式，直接返回:', response)
    return response
  },

  // 微信相关接口
  async getWechatConfig(url) {
    // 如果没有传入URL，使用当前页面URL（去除hash）
    const targetUrl = url || window.location.href.split('#')[0]
    const response = await wechatApi.getWechatConfig(targetUrl)
    console.log('微信配置API响应:', response)

    // 响应格式：{data: {code: 0, data: {appId, timestamp, nonceStr, signature}, msg: ""}, status: 200}
    const { data } = response

    if (data.code === 0) {
      console.log('✅ 微信配置获取成功:', data.data)
      return data.data
    } else {
      console.error('❌ 微信配置获取失败:', data.msg)
      throw new Error(data.msg || '微信配置获取失败')
    }
  },

  async getWechatAudioUrl(mediaId) {
    const response = await wechatApi.getWechatAudioUrl(mediaId)
    console.log('微信音频流API响应:', response)

    // 响应格式：直接返回二进制流(Blob)，不是JSON格式
    // 因为设置了 responseType: 'blob'，axios会直接返回Blob对象
    if (response.data instanceof Blob) {
      console.log('✅ 微信音频流获取成功，大小:', response.data.size, 'bytes')
      return response.data // 直接返回Blob对象
    } else {
      console.error('❌ 微信音频流获取失败，响应不是Blob格式:', response)
      throw new Error('微信音频流获取失败，响应格式错误')
    }
  },

}

// 导出各个模块的API（如果需要直接使用）
export { surveyApi, wechatApi }